import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { UserRole } from '@docsystem/shared';
import { Spinner } from '@/components/ui/misc';
import { EmptyState } from '@/components/common';
import { Button } from '@/components/ui/button';
import { landingRouteFor, useRestoreSession, useSession } from '@/features/auth/useSession';

/**
 * Client-side route protection.
 *
 * This is a UX affordance only — it stops a user landing on a screen they cannot
 * use. Every API call is independently authorised on the server, so bypassing this
 * guard yields empty screens and 403s, not data.
 */
export function RoleGuard({
  allow,
  children,
}: {
  allow: UserRole[];
  children: ReactNode;
}) {
  const { isAuthenticated, role } = useSession();
  const { restoring } = useRestoreSession();
  const location = useLocation();

  if (restoring) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-6 w-6 text-primary" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (role && !allow.includes(role)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-6">
        <EmptyState
          icon={ShieldAlert}
          title="You do not have access to this area"
          description="Your role does not permit this screen. If you believe this is wrong, contact your administrator."
          action={
            <Button asChild>
              <a href={landingRouteFor(role)}>Go to my dashboard</a>
            </Button>
          }
        />
      </div>
    );
  }

  return <>{children}</>;
}
